import { BaseStore } from './base.store';
import { RootStore } from './index';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { IStore } from '../shared/common';
import userPreferencesService from '@src/services/user-preferences.service';
import { ReportPreferences, UserPreferencesDTO } from '@src/dtos/user-preferences.dto';
import { DateFilterOptionNew, MainPages, UserPreferencesPageType } from '@src/utils/enums';
import moment from 'moment';
import { dateFormat, DateRangeObject } from '@src/utils/datesUtils';
import reportService from '@src/services/report.service';

export class UserPreferencesStore extends BaseStore implements IStore<UserPreferencesDTO>{
    public data: UserPreferencesDTO | undefined = undefined;
    public reportsPreferences: ReportPreferences[] = [];
    public isLoading: boolean = false;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            data: observable,
            reportsPreferences: observable,
            isLoading: observable,
            updateAllReportsDate: action,
            updateShowUSA: action,
            updateReportPreferences: action,
            protectionPreferencesAsstFirstTime: computed,
            updateLastSeenDate: action,
            lastSeenNotification: computed,
            reset: action,
            reportsTimeFrame: computed,
            reportsDate: computed,
            reportsShowUSA: computed
        });
    }

    get lastSeenNotification() {
        if (this.data && !this.data?.userPreferences.pages.notifications.lastSeenDate) {
            void this.updateLastSeenDate(this.rootStore.uiStore.mainPage === MainPages.CONNECTIONS);
        }
        return moment.utc(this.data?.userPreferences.pages.notifications.lastSeenDate);
    }

    get reportsTimeFrame() { return this.data?.userPreferences.pages.reportsPage.timeFrame || '' as DateFilterOptionNew; }

    get reportsDate() { return this.data?.userPreferences.pages.reportsPage.dateRange || ''; }

    get protectionPreferencesAsstFirstTime() { return this.data?.userPreferences.pages.protectionPage.protectionAssistantFirstTime; }

    get reportsShowUSA() { return this.data?.userPreferences.pages.reportsPage.showUSA ?? false; }

    public reset() {
        this.data = undefined;
    }

    public async load(fetchAllServices = false) {
        try {
            runInAction(() => {
                this.isLoading = true;
            });
            const { customerId, serviceId } = this.rootStore.customerStore;

            const userPreferences = await userPreferencesService.getUserPreferences(customerId, serviceId, fetchAllServices);

            runInAction(() => {
                this.data = userPreferences;
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    public async loadReportsList() {
        const { customerId, serviceId } = this.rootStore.customerStore;
        const reportsList = await reportService.getAvailableReports(customerId, serviceId);

        runInAction(() => {
            this.reportsPreferences = reportsList;
        });
    }

    public updateAllReportsDate(newReportsDisplayTab: DateFilterOptionNew, newDateRange?: DateRangeObject) {
        if (!this.data || !newReportsDisplayTab) return;
        const isRange = newReportsDisplayTab.includes('-');

        this.updateReportPreferences(isRange ? `${dateFormat(newDateRange?.from)}-${dateFormat(newDateRange?.to)}` : '',
            !isRange ? newReportsDisplayTab : '',
            this.data.userPreferences.pages.reportsPage.showUSA);
    }

    public updateShowUSA(showUSA: boolean) {
        if (!this.data) return;
        this.updateReportPreferences(this.data.userPreferences.pages.reportsPage.dateRange,
            this.data.userPreferences.pages.reportsPage.timeFrame,
            showUSA);
    }

    public updateReportPreferences(dateRange: string, timeFrame: DateFilterOptionNew | '', showUSA: boolean) {
        if (!this.data) return;
        const { customerId, serviceId } = this.rootStore.customerStore;
        runInAction(() => {
            this.data!.userPreferences.pages.reportsPage =
                {
                    dateRange: dateRange,
                    timeFrame: timeFrame,
                    showUSA: showUSA
                };
        });

        void userPreferencesService.updateReportPreferences(customerId, serviceId, UserPreferencesPageType.Report, this.data.userPreferences.pages.reportsPage);
    }

    public updateProtectionPageAsstToFalse() {
        const { customerId, serviceId } = this.rootStore.customerStore;
        void userPreferencesService.updateProtectionPreferences(customerId, serviceId, { protectionAssistantFirstTime: false });
        runInAction(() => {
            this.data!.userPreferences.pages.protectionPage.protectionAssistantFirstTime = false;
        });
    }

    public async updateLastSeenDate(fetchAllServices = false) {
        const { customerId, serviceId } = this.rootStore.customerStore;
        const lastSeenDate = moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS');
        await userPreferencesService.updateNotificationLastSeen(customerId, serviceId, fetchAllServices, { lastSeenDate });

        runInAction(() => {
            if (this.data) {
                this.data.userPreferences.pages.notifications = {
                    ...this.data.userPreferences.pages.notifications,
                    lastSeenDate
                };
            }
        });
    }
}
